import type { GameTypePlugin, GameRenderer, GameConfigurator, GameRendererCapabilities } from '../../interfaces/game-type-plugin.interface';
import { ScratchcardRenderer } from './scratchcard-renderer';
import type { ScratchcardGameSettings } from '@kanek/shared-data-access';

/**
 * Scratchcard Game Plugin
 *
 * Implements the GameTypePlugin interface for scratchcard games.
 * Uses scratchcard-js library for rendering.
 */
export class ScratchcardPlugin implements GameTypePlugin {
  readonly id = 'scratchcard';
  readonly name = 'Scratch Card';
  readonly description = 'Interactive scratch-off card game';
  readonly version = '1.0.0';

  readonly capabilities: GameRendererCapabilities = {
    mode: 'html-in-3d',          // HTML scratchcard + 3D card base
    requires3DScene: true,       // Now includes 3D lottery ticket card
    requiresHTMLContainer: true, // Needs HTML canvas for scratching
    supportsAR: true,            // Can be projected in AR
    supportsVR: false            // VR not yet supported
  };

  createRenderer(): GameRenderer {
    return new ScratchcardRenderer();
  }

  createConfigurator(): GameConfigurator {
    return {
      getDefaultConfig: (): ScratchcardGameSettings => ({
        type: 'scratchcard',
        scratchType: 'brush',
        containerWidth: 400,
        containerHeight: 300, // Match configurator default
        imageForwardSrc: '', // Will use default base64 SVG if empty
        imageBackgroundSrc: '', // Will use default base64 SVG if empty
        htmlBackground: '#cccccc',
        clearZoneRadius: 30,
        nPoints: 100,
        pointSize: 10,
        percentToFinish: 70,
        gridMode: true, // Enable grid mode by default (matches configurator)
        enableSoundEffects: true, // Enable by default (matches configurator)
        cardBackgroundColor: '#ffffff',
        prizeBackgroundColor: '#f5f5f5',
        
        // 3D Card defaults (synchronized with configurator)
        cardDepth: 0.1,
        cardMarginPercent: 0.05, // Match configurator default
        card3DColor: '#ffffff',
        cardBorderColor: '#ffd700', // Match configurator default
        cardRoughness: 0.3,
        cardMetalness: 0.5, // Match configurator default
        cardOpacity: 1.0,
        cardHeaderEnabled: true,
        cardHeaderHeightPercent: 0, // Set to 0% by default (matches configurator)
        cardHeaderColor: '#ed1f24',
        cardHeaderText: '',
        cardFooterEnabled: false, // Match configurator default
        cardFooterHeightPercent: 0, // Set to 0% by default (matches configurator)
        cardFooterColor: '#333333', // Match configurator default
        castShadow: true,
        receiveShadow: true
      }),

      validateConfig: (config: unknown): string[] => {
        const errors: string[] = [];
        const settings = config as ScratchcardGameSettings;

        // imageForwardSrc is optional - defaults are handled by config mapper
        // No validation needed for images

        if (settings.containerWidth < 200 || settings.containerWidth > 1000) {
          errors.push('Container width must be between 200 and 1000 pixels');
        }

        if (settings.containerHeight < 150 || settings.containerHeight > 800) {
          errors.push('Container height must be between 150 and 800 pixels');
        }

        if (settings.percentToFinish < 1 || settings.percentToFinish > 100) {
          errors.push('Percent to finish must be between 1 and 100');
        }

        return errors;
      },

      getConfigSchema: () => ({
        sections: [
          {
            title: 'Container Settings',
            fields: [
              {
                key: 'containerWidth',
                label: 'Container Width',
                type: 'number',
                defaultValue: 400,
                description: 'Width in pixels',
                validation: { min: 200, max: 1000 }
              },
              {
                key: 'containerHeight',
                label: 'Container Height',
                type: 'number',
                defaultValue: 300,
                description: 'Height in pixels',
                validation: { min: 150, max: 800 }
              }
            ]
          },
          {
            title: 'Scratch Behavior',
            fields: [
              {
                key: 'scratchType',
                label: 'Scratch Type',
                type: 'select',
                defaultValue: 'brush',
                options: [
                  { label: 'Brush', value: 'brush' },
                  { label: 'Spray', value: 'spray' },
                  { label: 'Circle', value: 'circle' },
                  { label: 'Line', value: 'line' }
                ]
              },
              {
                key: 'clearZoneRadius',
                label: 'Clear Zone Radius',
                type: 'number',
                defaultValue: 30,
                description: 'Size of scratch area in pixels',
                validation: { min: 10, max: 100 }
              },
              {
                key: 'percentToFinish',
                label: 'Percent to Finish',
                type: 'number',
                defaultValue: 70,
                description: 'Percentage of card to scratch before revealing',
                validation: { min: 1, max: 100, required: true }
              }
            ]
          },
          {
            title: 'Visual Settings',
            fields: [
              {
                key: 'imageForwardSrc',
                label: 'Scratch Layer Image URL',
                type: 'text',
                defaultValue: '',
                description: 'Optional: Image to scratch off (uses default if empty)'
              },
              {
                key: 'imageBackgroundSrc',
                label: 'Background Image URL',
                type: 'text',
                defaultValue: '',
                description: 'Optional: Revealed background image'
              },
              {
                key: 'htmlBackground',
                label: 'HTML Background',
                type: 'color',
                defaultValue: '#cccccc',
                description: 'Fallback color if no background image'
              }
            ]
          }
        ]
      })
    };
  }
}
